<template>
  <!-- Clickable trigger — wraps whatever the parent renders.
       Falls back to rendering src/alt when no slot content is provided (legacy usage). -->
  <div class="cursor-pointer" @click="openAt(startIndex)">
    <slot>
      <div v-if="src" :class="['overflow-hidden rounded-lg group', borderClass, shadowClass]">
        <img
          :src="src"
          :alt="alt"
          class="w-full h-auto object-cover transition-transform duration-300 ease-out group-hover:scale-[1.02]"
        />
      </div>
    </slot>
  </div>

  <!-- Full-screen lightbox, mounted on <body> -->
  <Teleport to="body">
    <Transition name="lb-fade">
      <div
        v-if="isOpen"
        ref="lightboxEl"
        class="fixed inset-0 z-[9999] bg-black/75 backdrop-blur-md flex flex-col outline-none select-none"
        tabindex="-1"
      >

        <!-- ── Top bar ── -->
        <div class="flex items-start justify-between px-6 pt-5 pb-3 shrink-0">
          <div class="flex flex-col gap-0.5">
            <p v-if="currentImage.title" class="text-white font-medium text-sm leading-snug">
              {{ currentImage.title }}
            </p>
            <p class="text-white/40 text-xs">{{ currentIndex + 1 }} of {{ imagesList.length }}</p>
          </div>
          <div class="flex items-center gap-1">
            <button
              @click.stop="toggleZoom"
              @mousedown.stop
              class="w-9 h-9 flex items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors"
              :title="zoomed ? 'Fit to screen' : 'Actual size'"
            >
              <svg v-if="!zoomed" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/><path d="M11 8v6M8 11h6"/>
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/><path d="M8 11h6"/>
              </svg>
            </button>
            <button
              @click.stop="close"
              @mousedown.stop
              class="w-9 h-9 flex items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors"
              title="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 6 6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- ── Image area ── -->
        <div
          ref="panContainer"
          class="flex-1 relative min-h-0 overflow-hidden"
          :class="[
            isDragging ? 'cursor-grabbing' : 'cursor-grab',
            !zoomed ? 'flex items-center justify-center px-14' : ''
          ]"
          @mousedown="onMouseDown"
          @mousemove="onMouseMove"
          @mouseup="onMouseUp"
          @mouseleave="onMouseUp"
          @touchstart.passive="onTouchStart"
          @touchend="onBgTouchEnd"
        >
          <!-- Prev / Next arrows (fit-to-screen mode) -->
          <button
            v-if="imagesList.length > 1 && !zoomed"
            @click.stop="prev"
            @mousedown.stop
            @touchend.stop="prev"
            class="absolute left-3 z-10 w-10 h-10 flex items-center justify-center rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="m15 18-6-6 6-6"/>
            </svg>
          </button>

          <!-- Main image -->
          <img
            ref="imageEl"
            :src="currentImage.src"
            :alt="currentImage.alt || ''"
            draggable="false"
            class="block [touch-action:manipulation]"
            :class="zoomed
              ? 'absolute max-w-none'
              : 'max-w-full max-h-[calc(100vh-160px)] object-contain'"
            :style="zoomed ? { left: panX + 'px', top: panY + 'px' } : {}"
            @dblclick.stop="toggleZoom"
            @touchstart.passive.stop="onTouchStart"
            @touchend.stop="onImageTouchEnd"
            @load="onImageLoad"
          />

          <button
            v-if="imagesList.length > 1 && !zoomed"
            @click.stop="next"
            @mousedown.stop
            @touchend.stop="next"
            class="absolute right-3 z-10 w-10 h-10 flex items-center justify-center rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="m9 18 6-6-6-6"/>
            </svg>
          </button>

          <!-- Compact nav arrows shown in zoomed mode -->
          <template v-if="imagesList.length > 1 && zoomed">
            <button
              @click.stop="prev"
              @mousedown.stop
              @touchend.stop="prev"
              class="absolute bottom-4 left-4 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-black/50 text-white/70 hover:text-white hover:bg-black/80 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="m15 18-6-6 6-6"/>
              </svg>
            </button>
            <button
              @click.stop="next"
              @mousedown.stop
              @touchend.stop="next"
              class="absolute bottom-4 right-4 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-black/50 text-white/70 hover:text-white hover:bg-black/80 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="m9 18 6-6-6-6"/>
              </svg>
            </button>
          </template>
        </div>

        <!-- ── Thumbnail strip ── -->
        <div
          v-if="imagesList.length > 1"
          class="shrink-0 flex items-center justify-center gap-2 px-6 py-4 overflow-x-auto"
        >
          <button
            v-for="(img, i) in imagesList"
            :key="i"
            @click.stop="goTo(i)"
            @mousedown.stop
            class="shrink-0 rounded overflow-hidden transition-all duration-200 ring-2 ring-offset-1 ring-offset-black cursor-pointer"
            :class="i === currentIndex ? 'ring-white opacity-100' : 'ring-transparent opacity-40 hover:opacity-70'"
          >
            <img :src="img.src" :alt="img.alt || ''" class="w-16 h-12 object-cover block" />
          </button>
        </div>

      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { ref, computed, onUnmounted } from 'vue';

const DRAG_THRESHOLD  = 5;   // px before a mousedown becomes a drag
const SWIPE_THRESHOLD = 60;  // px horizontal drag to trigger slide change

export default {
  name: 'imageLauncher',
  props: {
    src:         { type: String, default: '' },
    alt:         { type: String, default: '' },
    images:      { type: Array,  default: () => [] },
    startIndex:  { type: Number, default: 0 },
    borderClass: { type: String, default: '' },
    shadowClass: { type: String, default: '' },
  },
  setup(props) {
    const isOpen       = ref(false);
    const currentIndex = ref(props.startIndex);
    const zoomed       = ref(false);
    const lightboxEl   = ref(null);
    const panContainer = ref(null);
    const imageEl      = ref(null);

    // Pan state
    const panX = ref(0);
    const panY = ref(0);

    // Drag state (plain vars — no need for reactivity)
    const isDragging = ref(false);
    let isMouseDown  = false;
    let dragOnImage  = false;
    let dragStartX   = 0;
    let dragStartY   = 0;
    let panStartX    = 0;
    let panStartY    = 0;

    let scrollPosition = 0;

    const imagesList = computed(() =>
      props.images && props.images.length
        ? props.images
        : [{ src: props.src, alt: props.alt }]
    );

    const currentImage = computed(
      () => imagesList.value[currentIndex.value] ?? imagesList.value[0]
    );

    const resetPan = () => { panX.value = 0; panY.value = 0; };

    const centreImage = () => {
      const container = panContainer.value;
      const img = imageEl.value;
      if (!container || !img) return;
      panX.value = (container.clientWidth  - img.naturalWidth)  / 2;
      panY.value = (container.clientHeight - img.naturalHeight) / 2;
    };

    const onImageLoad = () => { if (zoomed.value) centreImage(); };

    const toggleZoom = () => {
      zoomed.value = !zoomed.value;
      if (zoomed.value) setTimeout(centreImage, 0);
      else resetPan();
    };

    // ── Touch events ────────────────────────────────────────────
    const DOUBLE_TAP_DELAY = 300;
    let lastTapTime = 0;
    let touchStartX = 0;
    let touchStartY = 0;

    const onTouchStart = (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    };

    const onImageTouchEnd = (e) => {
      const t   = e.changedTouches[0];
      const dx  = t.clientX - touchStartX;
      const adx = Math.abs(dx);
      const ady = Math.abs(t.clientY - touchStartY);

      // Swipe left/right → navigate (horizontal must dominate, not zoomed)
      if (!zoomed.value && adx > SWIPE_THRESHOLD && adx > ady) {
        dx < 0 ? next() : prev();
        return;
      }

      // Double-tap → toggle zoom (small movement only)
      if (adx < 15 && ady < 15) {
        const now = Date.now();
        if (now - lastTapTime < DOUBLE_TAP_DELAY) {
          e.preventDefault();
          toggleZoom();
          lastTapTime = 0;
        } else {
          lastTapTime = now;
        }
      }
    };

    const onBgTouchEnd = (e) => {
      const t   = e.changedTouches[0];
      const dx  = t.clientX - touchStartX;
      const adx = Math.abs(dx);
      const ady = Math.abs(t.clientY - touchStartY);

      // Swipe left/right → navigate
      if (!zoomed.value && adx > SWIPE_THRESHOLD && adx > ady) {
        dx < 0 ? next() : prev();
        return;
      }

      // Tap on background → close
      if (adx < 10 && ady < 10) close();
    };

    // ── Pointer events ──────────────────────────────────────────
    const onMouseDown = (e) => {
      if (e.button !== 0) return;
      isMouseDown      = true;
      isDragging.value = false;
      dragStartX = e.clientX;
      dragStartY = e.clientY;
      panStartX  = panX.value;
      panStartY  = panY.value;
      dragOnImage = imageEl.value
        ? (e.target === imageEl.value || imageEl.value.contains(e.target))
        : false;
    };

    const onMouseMove = (e) => {
      if (!isMouseDown) return;
      const dx = e.clientX - dragStartX;
      const dy = e.clientY - dragStartY;
      if (!isDragging.value && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) {
        isDragging.value = true;
      }
      if (isDragging.value && zoomed.value) {
        panX.value = panStartX + dx;
        panY.value = panStartY + dy;
      }
    };

    const onMouseUp = (e) => {
      if (!isMouseDown) return;
      isMouseDown = false;
      const dx = e.clientX - dragStartX;

      if (!isDragging.value) {
        // Pure click — close only if on the background (not the image)
        if (!dragOnImage) close();
      } else if (!zoomed.value) {
        // Horizontal drag in fit-screen mode → navigate slides
        if (dx < -SWIPE_THRESHOLD) next();
        else if (dx > SWIPE_THRESHOLD) prev();
      }
      isDragging.value = false;
    };

    // ── Navigation ──────────────────────────────────────────────
    const next = () => { zoomed.value = false; resetPan(); currentIndex.value = (currentIndex.value + 1) % imagesList.value.length; };
    const prev = () => { zoomed.value = false; resetPan(); currentIndex.value = (currentIndex.value - 1 + imagesList.value.length) % imagesList.value.length; };
    const goTo = (i) => { zoomed.value = false; resetPan(); currentIndex.value = i; };

    // ── Keyboard ────────────────────────────────────────────────
    const handleKey = (e) => {
      if (!isOpen.value) return;
      if (e.key === 'Escape')     close();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft')  prev();
    };

    // ── Open / Close ────────────────────────────────────────────
    const openAt = (index = 0) => {
      currentIndex.value = typeof index === 'number' ? index : 0;
      zoomed.value = false;
      resetPan();
      scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      document.body.style.top = `-${scrollPosition}px`;
      document.body.classList.add('overflow-hidden');
      isOpen.value = true;
      window.addEventListener('keydown', handleKey);
      setTimeout(() => lightboxEl.value?.focus(), 50);
    };

    const close = () => {
      isOpen.value = false;
      zoomed.value = false;
      resetPan();
      document.body.classList.remove('overflow-hidden');
      document.body.style.top = '';
      window.scrollTo(0, scrollPosition);
      window.removeEventListener('keydown', handleKey);
      currentIndex.value = props.startIndex;
    };

    onUnmounted(() => {
      window.removeEventListener('keydown', handleKey);
      document.body.classList.remove('overflow-hidden');
      document.body.style.top = '';
    });

    return {
      isOpen, currentIndex, zoomed, lightboxEl, panContainer, imageEl,
      panX, panY, isDragging,
      imagesList, currentImage,
      openAt, close, next, prev, goTo, toggleZoom, onImageLoad,
      onMouseDown, onMouseMove, onMouseUp,
      onTouchStart, onImageTouchEnd, onBgTouchEnd,
    };
  },
};
</script>

<style scoped>
.lb-fade-enter-active,
.lb-fade-leave-active { transition: opacity 0.18s ease; }
.lb-fade-enter-from,
.lb-fade-leave-to     { opacity: 0; }
</style>
